import { DEFAULT_STATUSES } from '../../TaskNode/constants';
import { getEffectiveDurationMinutes } from '../../../../../utils/taskDuration';
import { calculateWorkTimeRange } from './workTimeRange';

/**
 * Remaining estimated work across open choices.
 * `totalMinutes` is the longest route (single-number callers).
 * `minMinutes`~`maxMinutes` is the fastest-to-longest range.
 */
export function calculateRemainingTime(
  tasks,
  statuses = DEFAULT_STATUSES,
  nestedSprints = [],
  allSprints = [],
  pathPrefix = [],
) {
  const range = calculateWorkTimeRange(tasks, statuses, nestedSprints, allSprints, {
    remainingOnly: true,
    pathPrefix,
  });
  return {
    totalMinutes: range.maxMinutes,
    minMinutes: range.minMinutes,
    maxMinutes: range.maxMinutes,
    missingEstCount: range.missingEstCount,
    items: range.items,
  };
}

/**
 * Total estimated work across open choices. Longest route, for single-number callers.
 * A parent with a nested campaign ignores its own est. time.
 */
export function calculateTotalEstimatedTime(
  tasks,
  statuses = DEFAULT_STATUSES,
  nestedSprints = [],
  allSprints = [],
) {
  return calculateWorkTimeRange(tasks, statuses, nestedSprints, allSprints, {
    remainingOnly: false,
  }).maxMinutes;
}

/**
 * Effective est. time range for display. Nested campaigns roll up fastest~longest.
 *
 * @returns {{ minMinutes: number|null, maxMinutes: number|null }}
 */
export function getTaskEstimatedDurationRange(
  task,
  { statuses = DEFAULT_STATUSES, nestedSprints = [], allSprints = [] } = {},
) {
  if (!task) return { minMinutes: null, maxMinutes: null };

  if (task.nestedSprintId) {
    const nestedSprint = nestedSprints.find((ns) => ns.id === task.nestedSprintId)
      || allSprints.find((ns) => ns.id === task.nestedSprintId)
      || null;

    if (nestedSprint?.tasks?.length) {
      const nestedStatuses = nestedSprint.statuses || statuses;
      const grandchildSprints = allSprints.filter((s) => s.parentSprintId === nestedSprint.id);
      const range = calculateWorkTimeRange(
        nestedSprint.tasks,
        nestedStatuses,
        grandchildSprints,
        allSprints,
        { remainingOnly: false },
      );
      if (range.maxMinutes <= 0 && range.minMinutes <= 0) {
        return { minMinutes: null, maxMinutes: null };
      }
      return { minMinutes: range.minMinutes, maxMinutes: range.maxMinutes };
    }
    return { minMinutes: null, maxMinutes: null };
  }

  const minutes = getEffectiveDurationMinutes(task);
  return { minMinutes: minutes, maxMinutes: minutes };
}

/** Longest route when a nested campaign has choices. */
export function getTaskEstimatedDurationMinutes(task, options = {}) {
  const { maxMinutes } = getTaskEstimatedDurationRange(task, options);
  return maxMinutes > 0 ? maxMinutes : null;
}

/** True when est. time is derived from a nested campaign rollup (read-only on parent). */
export function isNestedEstimatedDurationRollup(task) {
  return !!task?.nestedSprintId;
}
